// 2026-06-25: Compact Clock-In/Out widget for Operator HMI header
// Self-contained: shows In button, or Clocked-In pill + Out button
import React, { useEffect, useState } from 'react';
import { clockIn, clockOut, getClockStatus } from '../../Services/app/clockservice';
import Swal from 'sweetalert2';

const COLORS = {
  orange: '#f97316',
  green: '#16a34a',
  red: '#dc2626',
  gray: '#6b7280',
  bg: '#f9fafb',
  border: '#e5e7eb',
};

function fmtElapsed(sec) {
  if (sec == null || sec < 0) return '0m';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function ClockWidget({ customerId, operator, machineId, machineName, shiftName }) {
  const [status, setStatus] = useState({ clocked_in: false, entry: null, elapsed_seconds: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);

  const operatorId = operator?.operatorid || operator?.id || '';
  const operatorName = operator?.operatorname || operator?.name || '';
  const ready = customerId && operatorId && operatorName;

  // poll status every 30s, tick every 10s for elapsed display
  useEffect(() => {
    if (!ready) return;
    let stop = false;
    async function fetchStatus() {
      try {
        const r = await getClockStatus({ customer_id: customerId, operator_id: operatorId, operator_name: operatorName });
        if (!stop) setStatus(r);
      } catch (e) {
        // silent — widget is non-critical
      }
    }
    fetchStatus();
    const idStatus = setInterval(fetchStatus, 30000);
    const idTick = setInterval(() => setTick(t => t + 1), 10000);
    return () => { stop = true; clearInterval(idStatus); clearInterval(idTick); };
  }, [ready, customerId, operatorId, operatorName]);

  // live elapsed without re-fetching: recompute from clock_in_ts on each tick
  const liveElapsedSec = status.clocked_in && status.entry?.clock_in_ts
    ? Math.floor((Date.now() - new Date(status.entry.clock_in_ts).getTime()) / 1000)
    : 0;

  async function handleIn() {
    if (!ready || loading) return;
    setLoading(true); setErr(null);
    try {
      const r = await clockIn({
        customer_id: customerId,
        operator_id: operatorId,
        operator_name: operatorName,
        operator_role: 'OPERATOR',
        machine_id: machineId || null,
        machine_name: machineName || null,
        shift_name: shiftName || null,
        activity: 'RUN',
        source: 'hmi',
      });
      setStatus({
        clocked_in: true,
        entry: r.entry,
        elapsed_seconds: 0,
      });
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Clock-in failed');
    } finally {
      setLoading(false);
    }
  }

  // Phase 13 2026-06-26: confirm + pause running op before clock out
  async function pauseRunningBeforeOut() {
    if (!status.entry?.wo_number || !status.entry?.op_id) return; // nothing running on this clock entry
    try {
      const axios = (await import('axios')).default;
      const base = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
      await axios.post(`${base}/api/workorder/operation/${status.entry.op_id}/pause`, {
        customer_id: customerId,
        operator_id: operatorId,
        operator_name: operatorName,
        machine_id: status.entry.machine_id || null,
        machine_name: status.entry.machine_name || null,
      });
    } catch (e) {
      console.warn('pause before clock-out failed:', e?.message);
    }
  }

  async function handleOut() {
    if (!ready || loading) return;
    // Phase 13: if an op is running (wo_number set on entry), confirm pause + exit
    if (status.entry?.wo_number) {
      const wo = status.entry.wo_number;
      const op = status.entry.op_seq || '';
      const result = await Swal.fire({
        title: 'Pause this operation?',
        html: '<div style="text-align:left;font-size:14px;line-height:1.5;color:#374151"><div><strong>' + wo + '</strong> · ' + op + ' is currently <strong style="color:#16a34a">RUNNING</strong>.</div><div style="margin-top:10px">Clocking out now will <strong>pause</strong> this operation.<br/>You can resume it when you clock back in.</div></div>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Pause & Clock Out',
        cancelButtonText: 'Keep working',
        confirmButtonColor: '#f97316',
        cancelButtonColor: '#6b7280',
        reverseButtons: true,
        focusCancel: true,
      });
      if (!result.isConfirmed) return;
      await pauseRunningBeforeOut();
    }
    setLoading(true); setErr(null);
    try {
      await clockOut({
        customer_id: customerId,
        operator_id: operatorId,
        operator_name: operatorName,
      });
      setStatus({ clocked_in: false, entry: null, elapsed_seconds: 0 });
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Clock-out failed');
    } finally {
      setLoading(false);
    }
  }

  if (!ready) {
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                    background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8,
                    fontSize: 12, color: COLORS.gray }}>
        Clock: sign in to start
      </div>
    );
  }

  if (status.clocked_in) {
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                      background: '#ffffff', border: `2px solid ${COLORS.green}`, borderRadius: 8,
                      fontSize: 14, fontWeight: 800, color: '#064e3b',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <span style={{ width: 10, height: 10, borderRadius: 5, background: COLORS.green,
                         display: 'inline-block', animation: 'pulse 2s infinite',
                         boxShadow: '0 0 0 2px rgba(22,163,74,0.25)' }} />
          {status.entry?.wo_number ? (
            <>
              <span>{status.entry.wo_number}</span>
              {status.entry.op_seq && <span style={{ color: '#064e3b' }}>· {status.entry.op_seq}</span>}
            </>
          ) : <span style={{ letterSpacing: 0.5 }}>CLOCKED IN</span>}
          {status.entry?.machine_name && <span style={{ color: '#111827', fontWeight: 800 }}>· {status.entry.machine_name}</span>}
          <span style={{ color: '#111827', fontWeight: 800, fontFamily: 'monospace' }}>· {fmtElapsed(liveElapsedSec)}</span>
        </div>
        <button onClick={handleOut} disabled={loading} title="Clock out"
                style={{ padding: '6px 14px', background: loading ? COLORS.gray : COLORS.red,
                         color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700,
                         cursor: loading ? 'wait' : 'pointer' }}>
          {loading ? '...' : 'CLOCK OUT'}
        </button>
        {err && <span style={{ fontSize: 11, color: COLORS.red, fontWeight: 700 }}>{err}</span>}
      </div>
    );
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button onClick={handleIn} disabled={loading} title="Clock in to current machine"
              style={{ padding: '6px 14px', background: loading ? COLORS.gray : COLORS.orange,
                       color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700,
                       cursor: loading ? 'wait' : 'pointer' }}>
        {loading ? '...' : 'CLOCK IN'}
      </button>
      {machineName && <span style={{ fontSize: 11, color: COLORS.gray }}>→ {machineName}</span>}
      {err && <span style={{ fontSize: 11, color: COLORS.red, fontWeight: 700 }}>{err}</span>}
    </div>
  );
}
